import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  CircularProgress,
} from '@mui/material';
import {
  Remove as MinusIcon,
  Add as PlusIcon,
  SwapHoriz as ScaleIcon,
} from '@mui/icons-material';
import { Recipe } from '../models/Recipe';
import { AgentProgressEvent } from '../types/agentTypes';
import OttoThinking from './OttoThinking';

interface RecipeScalerProps {
  recipe: Recipe;
  onScaleChange: (scaledRecipe: Recipe, scale: number) => void;
}

const RecipeScaler: React.FC<RecipeScalerProps> = ({ recipe, onScaleChange }) => {
  const currentServings = recipe.servings || 4;
  const [targetServings, setTargetServings] = useState(currentServings);
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(String(currentServings));
  const [scaling, setScaling] = useState(false);
  const [progress, setProgress] = useState<AgentProgressEvent | null>(null);

  const hasChanged = targetServings !== currentServings;

  const handleIncrement = () => {
    setTargetServings(prev => Math.min(prev + 1, 99));
  };

  const handleDecrement = () => {
    setTargetServings(prev => Math.max(prev - 1, 1));
  };

  const handleEditStart = () => {
    setIsEditing(true);
    setEditValue(String(targetServings));
  };

  const handleEditSave = () => {
    const value = parseInt(editValue) || currentServings;
    setTargetServings(Math.max(1, Math.min(99, value)));
    setIsEditing(false);
  };

  const handleEditKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleEditSave();
    } else if (e.key === 'Escape') {
      setIsEditing(false);
      setEditValue(String(targetServings));
    }
  };

  const applyScaling = async () => {
    if (!hasChanged) {
      return;
    }

    setScaling(true);
    setProgress(null);
    try {
      const scaleFactor = targetServings / currentServings;

      // Use agent service for intelligent scaling
      const agentService = (await import('../services/agentService')).default;
      const result = await agentService.scaleRecipe({
        recipe: recipe,
        scaleFactor: scaleFactor,
        targetServings: targetServings,
        onProgress: (event) => setProgress(event),
      });

      const scaledRecipe = result.modifiedRecipe;

      if (!scaledRecipe) {
        throw new Error('No scaled recipe returned from agent');
      }

      // Update servings field
      scaledRecipe.servings = targetServings;

      console.log(`✅ Recipe scaled from ${currentServings} to ${targetServings} servings`);
      if (result.changes && result.changes.length > 0) {
        console.log('📝 Changes made:', result.changes.map(c => c.description).join(', '));
      }

      onScaleChange({ ...scaledRecipe }, scaleFactor);
    } catch (error) {
      console.error('Error scaling recipe:', error);
      alert('Failed to scale recipe. Please try again.');
    } finally {
      setScaling(false);
      setProgress(null);
    }
  };

  return (
    <>
      <Box sx={{
        mb: 2,
        p: 2,
        backgroundColor: 'rgba(139, 69, 19, 0.05)',
        borderRadius: 2,
        border: '1px solid rgba(139, 69, 19, 0.2)',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          {/* Left side: Title and current servings */}
          <Box>
            <Typography variant="subtitle2" sx={{
              fontWeight: 600,
              fontFamily: '"Crimson Text", serif',
              color: '#5D4037',
              mb: 0.5,
            }}>
              Recipe Scaling
            </Typography>
            <Typography variant="body2" sx={{
              fontFamily: '"Crimson Text", serif',
              color: 'rgba(139, 69, 19, 0.7)',
            }}>
              Current: {currentServings} serving{currentServings !== 1 ? 's' : ''}
            </Typography>
          </Box>

          {/* Right side: Controls */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{
              fontFamily: '"Crimson Text", serif',
              color: '#5D4037',
              mr: 0.5,
            }}>
              Scale to:
            </Typography>

            {/* Minus button */}
            <IconButton
              size="small"
              onClick={handleDecrement}
              disabled={targetServings <= 1 || scaling}
              sx={{
                color: '#8B4513',
                border: '1px solid rgba(139, 69, 19, 0.3)',
                '&:hover': {
                  backgroundColor: 'rgba(139, 69, 19, 0.1)',
                },
                '&:disabled': {
                  opacity: 0.4,
                },
              }}
            >
              <MinusIcon fontSize="small" />
            </IconButton>

            {/* Servings display/input */}
            {isEditing ? (
              <TextField
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onBlur={handleEditSave}
                onKeyDown={handleEditKeyDown}
                type="number"
                inputProps={{ min: 1, max: 99 }}
                size="small"
                autoFocus
                sx={{
                  width: '60px',
                  '& .MuiOutlinedInput-root': {
                    fontFamily: '"Crimson Text", serif',
                    fontSize: '1rem',
                  },
                }}
              />
            ) : (
              <Button
                onClick={handleEditStart}
                disabled={scaling}
                sx={{
                  minWidth: '60px',
                  fontFamily: '"Crimson Text", serif',
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  color: hasChanged ? '#FF6B35' : '#5D4037',
                  textTransform: 'none',
                  p: 0.5,
                  '&:hover': {
                    backgroundColor: 'rgba(139, 69, 19, 0.1)',
                  },
                }}
              >
                {targetServings}
              </Button>
            )}

            {/* Plus button */}
            <IconButton
              size="small"
              onClick={handleIncrement}
              disabled={targetServings >= 99 || scaling}
              sx={{
                color: '#8B4513',
                border: '1px solid rgba(139, 69, 19, 0.3)',
                '&:hover': {
                  backgroundColor: 'rgba(139, 69, 19, 0.1)',
                },
                '&:disabled': {
                  opacity: 0.4,
                },
              }}
            >
              <PlusIcon fontSize="small" />
            </IconButton>

            {/* Apply button - only show when changed */}
            {hasChanged && (
              <Button
                variant="contained"
                size="small"
                startIcon={scaling ? <CircularProgress size={14} sx={{ color: 'white' }} /> : <ScaleIcon />}
                onClick={applyScaling}
                disabled={scaling}
                sx={{
                  ml: 1,
                  textTransform: 'none',
                  fontFamily: '"Crimson Text", serif',
                  backgroundColor: '#8B4513',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: '#6D4037',
                  },
                  '&:disabled': {
                    backgroundColor: 'rgba(139, 69, 19, 0.5)',
                  },
                }}
              >
                {scaling ? 'Scaling...' : 'Apply'}
              </Button>
            )}
          </Box>
        </Box>
      </Box>

      {/* Otto thinking animation overlay */}
      <OttoThinking progress={scaling && progress ? progress : undefined} size={60} />
    </>
  );
};

export default RecipeScaler;
